/* eslint-disable max-lines-per-function */
import { useEffect, useState } from 'react';

import { ChevronLeftIcon, ChevronRightIcon } from '../../index';
import { addStyles } from '../../utils/helpers';

export interface PaginationProps {
  current: number;
  total: number;
  numDisplayed: number;
  onPageChange: (page: number) => void;
  className?: string;
}

const options = {
  base: 'size-11 rounded-lg text-sm text-center',
  inactive: 'text-gray-900 hover:bg-gray-200',
  active: 'text-white bg-primary',
  ellipsis: 'hover:bg-transparent cursor-default',
};

const calculatePages = (
  current: PaginationProps['current'],
  total: PaginationProps['total'],
  numDisplayed: PaginationProps['numDisplayed']
) => {
  const ceil = Math.ceil((numDisplayed - 1) / 2);
  let min = current - ceil;
  let max = current + ceil;

  // prevent displaying negative page numbers
  if (min <= 0) {
    max = max + (min - 1) * -1;
    min = 1;
  }

  // prevent displaying numbers beyond the last page
  if (max >= total) {
    min = min + (total - max);
    min = min <= 0 ? 1 : min;
    max = total;
  }

  // create an array of the page range
  const pages: Array<number | string> = Array.from({ length: max - min + 1 }, (val, ind) => ind + min);

  // if total is far away from max
  if (max !== total) {
    pages.splice(-1, 1);
    pages.push('-', total);
  }

  // if first page is far away from min
  if (min !== 1) {
    pages.splice(0, 1);
    pages.unshift(1, '-');
  }

  return pages;
};

export function Pagination({ current = 1, total = 5, numDisplayed = 5, ...props }: PaginationProps) {
  const { className, onPageChange } = props;
  const [page, setPage] = useState(current);

  useEffect(() => {
    setPage(current);
  }, [current]);

  const shownPages = calculatePages(page, total, numDisplayed);

  const changePage = (event: React.MouseEvent<HTMLButtonElement>) => {
    const pageNum: number = +event.currentTarget.value;

    if (Number.isNaN(pageNum)) {
      return;
    }

    setPage(pageNum);
    onPageChange(pageNum);
  };

  // Don't show if only one page
  if (total <= 1) {
    return <></>;
  }

  return (
    <div>
      <nav role="navigation" className={className}>
        <ul className="flex flex-wrap items-center gap-x-2">
          {/* Don't show if on first page  */}
          {page !== 1 && (
            <li>
              <button
                onClick={changePage}
                value={page - 1}
                className={addStyles([options.base, options.inactive])}
                aria-label="Go to previous page"
              >
                <ChevronLeftIcon height="1rem" width="1rem" className="mx-auto fill-gray-900" />
              </button>
            </li>
          )}

          {shownPages.map((pageNum, index) => {
            return (
              <li key={index}>
                <button
                  onClick={changePage}
                  value={pageNum}
                  aria-current={pageNum === page ? 'true' : 'false'}
                  className={addStyles([
                    options.base,
                    pageNum === page ? options.active : options.inactive,
                    pageNum === '-' ? options.ellipsis : '',
                  ])}
                >
                  {pageNum === '-' ? <span>&hellip;</span> : pageNum}
                </button>
              </li>
            );
          })}

          {/* Don't show if on last page  */}
          {page !== total && (
            <li>
              <button
                onClick={changePage}
                value={page + 1}
                className={addStyles([options.base, options.inactive])}
                aria-label="Go to next page"
              >
                <ChevronRightIcon height="1rem" width="1rem" className="mx-auto fill-gray-900" />
              </button>
            </li>
          )}
        </ul>
      </nav>
    </div>
  );
}

export default Pagination;
